import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { NmsCustomTextConfig } from "@nms-angular-toolkit/nms-custom-text";
import { SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { StatusConfiguration } from "@nms-angular-toolkit/nms-status";
import { NmsAutoPollingService } from "@nms-ng2/app/shared/components/elements/nms-auto-updater/nms-auto-polling.service";
import { RefreshIntervalService } from "@nms-ng2/app/shared/services/refresh-interval/refresh-interval.service";
import { ANGULARJS_ROOTSCOPE, ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { SimpleModalComponent } from "ngx-simple-modal";
import { Observable, Subscription } from "rxjs";
import { CircuitDiagram } from "../diagram/models/diagram.models";
import { PopoverVisibilityManagerService } from "../diagram/popover/popover-visibility-manager.service";
import { CIRCUIT_STATUS, DwdmCircuitDiagnosticData } from "../dwdm-circuit.model";
import { CircuitStructuralChangeDetectorService } from "../services/circuit-structural-change-detector.service";
import { CircuitService } from "../services/circuit.service";
import { DiagnosticTypeButton } from "./dwdm-circuit-diagnostic-model";
import { DiagnosticTableColumnsService } from "./table/diagnostic-table-columns.service";
import { DwdmDiagramDiagnosticConverterService } from "./diagram/dwdm-diagram-diagnostic-converter.service";
import {
    AutoUpdaterDataResult,
    UpdateDetail
} from "@nms-ng2/app/shared/components/elements/nms-auto-updater/nms-auto-updater.component";
import { PollingRequestClient } from "@nms-ng2/app/shared/models/polling-request-client";

/**
 * Modal de visualização do diagnóstico do circuito.
 */
@Component({
    selector: "dwdm-circuit-diagnostic",
    templateUrl: "./dwdm-circuit-diagnostic.component.html",
    styleUrls: ["./dwdm-circuit-diagnostic.component.scss"],
    providers: [NmsAutoPollingService]
})
export class DwdmCircuitDiagnosticComponent
        extends SimpleModalComponent<{ circuitId: number }, any> implements OnInit, OnDestroy {
    circuitId: number;
    diagnosticData: DwdmCircuitDiagnosticData;

    pageTitle: string = "dwdm.circuit.modal.diagnostic.title";
    circuitDiagnosticConfig: NmsCustomTextConfig;
    statusConfiguration: StatusConfiguration;
    isButtonDiagramActive: boolean = true;
    isButtonTableActive: boolean = false;

    transceiversHundredColumns: SimpleTableColumnConfig[] = [];
    transceiversHundredColumnsSortable: Record<string, boolean>;

    transceiversFourHundredColumns: SimpleTableColumnConfig[] = [];
    transceiversFourHundredColumnsSortable: Record<string, boolean>;

    amplifiersColumns: SimpleTableColumnConfig[] = [];
    amplifiersColumnsSortable: Record<string, boolean>;

    oscColumns: SimpleTableColumnConfig[] = [];
    oscColumnsSortable: Record<string, boolean>;

    linksColumns: SimpleTableColumnConfig[] = [];
    linksColumnsSortable: Record<string, boolean>;

    alarmsColumns: SimpleTableColumnConfig[] = [];
    alarmsColumnsSortable: Record<string, boolean>;

    circuitDiagram: CircuitDiagram;

    autoUpdateInterval: number;

    isLoading: boolean = true;
    diagnosticSubscription: Subscription;

    constructor(private readonly diagnosticColumnsService: DiagnosticTableColumnsService,
            private readonly dwdmDiagramConverterService: DwdmDiagramDiagnosticConverterService,
            private readonly viewContainerRef: ViewContainerRef,
            private readonly nmsAutoPollingService: NmsAutoPollingService,
            private readonly refreshIntervalService: RefreshIntervalService,
            private readonly circuitService: CircuitService,
            private readonly structuralChangeDetector: CircuitStructuralChangeDetectorService,
            @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any,
            private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService) {
        super();

        this.refreshIntervalService.getRefreshInterval((interval: number) => {
            this.autoUpdateInterval = interval;
        });
    }

    ngOnInit() {
        this.loadDiagnosticData();
        this.options.closeOnClickOutside = false;
        this.initTableColumns();
    }

    autoUpdaterSetup() {
        const pollingClient = PollingRequestClient.DWDM_CIRCUIT_DIAGNOSTIC;
        this.nmsAutoPollingService.setup(this.extractDevicesResourceIds(), pollingClient, this.loadCircuit);
    }

    onClickActions(buttonType: DiagnosticTypeButton) {
        this.isButtonDiagramActive = buttonType === "diagram";
        this.isButtonTableActive = buttonType === "table";
    }

    requestPolling = (): Observable<Object> => {
        return this.nmsAutoPollingService.startDevicesPolling();
    };

    loadCircuit = (): Observable<DwdmCircuitDiagnosticData> => {
        return this.circuitService.getDiagnostic(this.circuitId);
    };

    notifyUpdateData(updateResult: AutoUpdaterDataResult) {
        const hasStructuralChanges = this.structuralChangeDetector
            .hasChange(this.diagnosticData.circuit, updateResult.data.circuit);
        if (hasStructuralChanges) {
            this.rootScope
            .showDialog({
                message: this.translate.instant("dwdm.circuit.modal.diagnostic.diagram.structural.change"),
                isConfirm: true
            })
            .then(() => this.confirmUpdate(updateResult.data))
            .catch(() => {
                /* Não faz nada mas precisa declarar no caso de cancel */
            })
        } else {
            //Não havendo mudanças estruturais, será avaliado se existem cards fixados para merca-los como desatualizados.
            const hasOpenedPopovers = this.popoverVisibilityManagerService.hasFixedPopovers();
            if (hasOpenedPopovers) {
                this.popoverVisibilityManagerService.outdateAll();
            }
            this.fillModalContent(updateResult.data)
        }
    }

    /**
     * Após a confirmação do usuário, os popovers são fechados e os dados são atualizados.
     * @param data Dados da modal de diagnóstico.
     */
    private confirmUpdate(data) {
        this.popoverVisibilityManagerService.destroy()
        this.fillModalContent(data)
    }

    getPollingFeedbackTooltip(): Array<UpdateDetail> {
        return this.nmsAutoPollingService.pollingFeedbackMessage;
    }

    ngOnDestroy(): void {
        this.diagnosticSubscription?.unsubscribe();
    }

    private loadDiagnosticData() {
        this.diagnosticSubscription = this.circuitService.getDiagnostic(this.circuitId).subscribe((data) => {
            this.fillModalContent(data);
            this.autoUpdaterSetup();
        }, error => this.close() );
    }

    private initTableColumns() {
        this.transceiversHundredColumnsSortable = this.diagnosticColumnsService.buildTransceiversHundredColumnsSortable();
        this.transceiversHundredColumns = this.diagnosticColumnsService.buildTransceiversHundredColumns();

        this.transceiversFourHundredColumnsSortable = this.diagnosticColumnsService.buildTransceiversFourHundredColumnsSortable();
        this.transceiversFourHundredColumns = this.diagnosticColumnsService.buildTransceiversFourHundredColumns();

        this.amplifiersColumnsSortable = this.diagnosticColumnsService.buildAmplifiersColumnsSortable();
        this.amplifiersColumns = this.diagnosticColumnsService.buildAmplifiersColumns();

        this.oscColumnsSortable = this.diagnosticColumnsService.buildOscColumnsSortable();
        this.oscColumns = this.diagnosticColumnsService.buildOscColumns();

        this.linksColumnsSortable = this.diagnosticColumnsService.buildLinkColumnsSortable();
        this.linksColumns = this.diagnosticColumnsService.buildLinkColumns();

        this.alarmsColumnsSortable = this.diagnosticColumnsService.buildAlarmColumnsSortable();
        this.alarmsColumns = this.diagnosticColumnsService.buildAlarmColumns();
    }

    private fillModalContent(data: DwdmCircuitDiagnosticData) {
        this.diagnosticData = data;
        this.circuitDiagram = null;
        this.circuitDiagram = this.dwdmDiagramConverterService.convert(this.diagnosticData, this.viewContainerRef);
        this.isLoading = false;

        this.circuitDiagnosticConfig = {
            id: "circuit-name-info",
            text: this.diagnosticData.circuitName,
            hasTooltip: true,
            tooltipContent: this.diagnosticData.circuitDescription
        };

        const circuitStatusProperties = CIRCUIT_STATUS[this.diagnosticData.circuitStatus];
        const circuitStatusLabel = this.translate.instant(circuitStatusProperties.translationKey);
        this.statusConfiguration = {
            label: circuitStatusLabel,
            bgColor: circuitStatusProperties.bgColor
        };
    }

    private extractDevicesResourceIds(): number[] {
        const ends = [
            this.diagnosticData.circuit.endsStepModel.aEndDevice.id,
            this.diagnosticData.circuit.endsStepModel.zEndDevice.id
        ];
        const intermediates = this.diagnosticData.circuit.intermediatesStepModel.devices.map((device) => device.resourceId) || [];

        return [...ends, ...intermediates];
    }
}
